import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsNativeIOS } from "@/hooks/useIsNativeIOS";
import { NativeSubscriptionSurface } from "@/components/NativeSubscriptionSurface";
import { logEffectiveAccess } from "@/lib/access-engine";
import { useEffectiveAccess } from "@/hooks/useEffectiveAccess";
import { PricingPlanSelector } from "@/components/PricingPlanSelector";
import { PaywallNavHeader } from "@/components/PaywallNavHeader";
import { useSubscriptionContext, clearSubscriptionCache, getSubscriptionCacheDiagnostics } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { safeCacheRefreshWithTimeout } from "@/lib/safe-cache-refresh";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { captureSentryEvent } from "@/lib/sentry";

// Module-level memo of the last "ready" access decision per user.
// Lets SubscriptionRoute render children optimistically on a route change
// while a transient sub-check (e.g. admin re-validation) is still loading.
// Cleared on user change inside the component via useEffect below.
const lastReadyAccessByUserId = new Map<string, { canTakeQuiz: boolean; role: string }>();

export const SubscriptionRoute = ({ children }: { children: React.ReactNode }) => {
  const access = useEffectiveAccess();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isNativeIOS = useIsNativeIOS();
  const { user } = useAuth();
  const subscription = useSubscriptionContext();
  const queryClient = useQueryClient();
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [versionCheckReady, setVersionCheckReady] = useState(false);
  const [versionMismatchBlocked, setVersionMismatchBlocked] = useState(false);
  const [syncingAccess, setSyncingAccess] = useState(false);
  const pathname = access.input.pathname;
  const isRealExamRoute = pathname.includes('/real-exam');
  const runningBundleVersion = (import.meta.env.VITE_APP_VERSION as string) || 'dev';
  const shouldStrictVersionCheck = runningBundleVersion !== 'dev' && !import.meta.env.DEV;

  // Record every "ready" decision so a later transient loading state can
  // reuse it (optimistic render). Drop entry on user change.
  useEffect(() => {
    if (access.status === "ready" && user?.id) {
      lastReadyAccessByUserId.set(user.id, {
        canTakeQuiz: access.canTakeQuiz,
        role: access.role,
      });
    }
  }, [access.status, access.canTakeQuiz, access.role, user?.id]);
  useEffect(() => {
    // Clear entries for users that are no longer signed in.
    if (!user?.id) lastReadyAccessByUserId.clear();
  }, [user?.id]);

  useEffect(() => {
    if (!isRealExamRoute) return;

    const diagnostics = getSubscriptionCacheDiagnostics(user?.id);
    console.log('🩺 [RealExam][RouteAccessDiag]', {
      user_id: user?.id ?? null,
      email: user?.email ?? null,
      serverVersion,
      runningBundleVersion,
      hasActiveSub: subscription.hasActiveSubscription,
      hasCohortAccess: subscription.hasCohortAccess,
      hasAccess: access.canTakeQuiz,
      subscription_cache_timestamp: diagnostics.lastCheckedAt
        ? new Date(diagnostics.lastCheckedAt).toISOString()
        : null,
      accessSource: diagnostics.valid ? 'cache+server_revalidated' : 'server',
      browser_device: {
        userAgent: navigator.userAgent,
        platform: navigator.platform || 'unknown',
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        route: pathname,
      },
      current_route: pathname,
      error_exacto_si_bloquea: access.canTakeQuiz ? null : access.reason,
      subscription_cache_state: diagnostics,
      versionMismatchBlocked,
    });
  }, [access.canTakeQuiz, access.reason, isRealExamRoute, pathname, runningBundleVersion, serverVersion, subscription.hasActiveSubscription, subscription.hasCohortAccess, user?.email, user?.id, versionMismatchBlocked]);

  useEffect(() => {
    if (!isRealExamRoute || !user?.id) return;

    const diagnostics = getSubscriptionCacheDiagnostics(user.id);
    if (!diagnostics.exists || diagnostics.valid) return;

    console.log('🧹 [RealExam][RouteAccessDiag] Ignoring stale/invalid subscription cache and revalidating', diagnostics);
    clearSubscriptionCache();
    void subscription.revalidate();
  }, [isRealExamRoute, subscription, user?.id]);

  useEffect(() => {
    let cancelled = false;

    const checkVersion = async () => {
      if (!isRealExamRoute || !shouldStrictVersionCheck) {
        if (!cancelled) {
          setVersionMismatchBlocked(false);
          setVersionCheckReady(true);
        }
        return;
      }

      try {
        const res = await fetch(`/manifest-version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
        });
        if (!res.ok) throw new Error(`manifest_version_http_${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setServerVersion(data?.version ?? null);
        setVersionMismatchBlocked(!!data?.version && data.version !== runningBundleVersion);
      } catch {
        if (cancelled) return;
        setVersionMismatchBlocked(false);
      } finally {
        if (!cancelled) setVersionCheckReady(true);
      }
    };

    void checkVersion();
    return () => {
      cancelled = true;
    };
  }, [isRealExamRoute, runningBundleVersion, shouldStrictVersionCheck]);

  const handleSyncAccess = useCallback(async () => {
    if (syncingAccess) return;

    setSyncingAccess(true);
    try {
      const diagnostics = getSubscriptionCacheDiagnostics(user?.id);
      console.log('🔄 [RealExam][SubscriptionRoute][SyncAccess]', {
        user_id: user?.id,
        route: pathname,
        diagnostics,
      });

      await supabase.auth.getSession();
      await supabase.auth.refreshSession();

      if (diagnostics.corrupted || (diagnostics.exists && !diagnostics.valid)) {
        clearSubscriptionCache();
      }

      await subscription.revalidate();
      await queryClient.invalidateQueries({ queryKey: ['institutionalStudent', user?.id] });
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      await queryClient.invalidateQueries({ queryKey: ['subscription'] });

      window.location.replace(pathname);
    } finally {
      setSyncingAccess(false);
    }
  }, [pathname, queryClient, subscription, syncingAccess, user?.id]);

  // Redirect on denied
  useEffect(() => {
    if (access.redirectTo) {
      navigate(access.redirectTo, { replace: true });
    }
  }, [access.redirectTo, navigate]);

  // Reusable spinner — used while loading AND while a redirect is in flight,
  // so the user never sees a flash of "access denied"/paywall content before
  // navigation happens.
  const renderSpinner = () => (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-background to-muted/20">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">{t("subscription.checking")}</p>
      </div>
    </div>
  );

  // ── Redirect pending — show spinner, never the paywall/denied UI ──
  // Without this guard, a "denied" decision with a non-null redirectTo
  // (not_authenticated, email_not_confirmed, needs_password_setup, user_blocked)
  // would briefly render the paywall before useNavigate runs.
  if (access.redirectTo) {
    return renderSpinner();
  }

  // ── Loading ──
  if (access.status === "loading") {
    // OPTIMISTIC RENDER: if SubscriptionContext is already loaded AND we
    // previously resolved a "ready" decision that granted access for this
    // user, skip the spinner and render children. Avoids the redundant
    // "Verificando suscripción…" screen on route changes after the very
    // first resolution. RLS still protects paid content server-side, and
    // the redirect effect handles the unlikely case the access flips.
    const lastReady = user?.id ? lastReadyAccessByUserId.get(user.id) : undefined;
    const safeOptimistic =
      subscription.isInitiallyLoaded &&
      !!lastReady &&
      (lastReady.canTakeQuiz || lastReady.role === "admin");
    if (safeOptimistic) {
      return <>{children}</>;
    }
    logEffectiveAccess("SubscriptionRoute", access);
    return renderSpinner();
  }

  if (isRealExamRoute && versionCheckReady && versionMismatchBlocked) {
    return (
      <Dialog open>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Actualizamos MBLearn</DialogTitle>
            <DialogDescription>Presiona actualizar para continuar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>serverVersion: {serverVersion ?? 'unknown'}</p>
            <p>runningBundleVersion: {runningBundleVersion}</p>
          </div>
          <DialogFooter>
            <Button onClick={() => void safeCacheRefreshWithTimeout(queryClient)} className="w-full sm:w-auto">
              Actualizar ahora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Access granted ──
  // For institutional/cohort roles, only allow through if canTakeQuiz is true
  // (baseline required or compliance block will set canTakeQuiz=false)
  if (access.canTakeQuiz || access.role === "admin") {
    return <>{children}</>;
  }

  // Institutional/cohort users blocked by baseline — redirect to dashboard
  // (weekly compliance gate retired 2026-07)
  if ((access.role === "institutional" || access.role === "cohort") && !access.canTakeQuiz) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <PaywallNavHeader />
        <div className="flex min-h-[calc(100vh-56px)] items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <Lock className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            {t("institutional.baselineRequired") || "Debes completar tu examen Baseline primero"}
          </h2>
          <p className="text-muted-foreground text-sm">
            {t("institutional.completeBaselineToUnlock") || "Ve a tu dashboard para iniciar el examen Baseline."}
          </p>
          <Button
            onClick={() => window.location.href = "/dashboard"}
            variant="outline"
            size="lg"
          >
            {t("subscription.backToDashboard")}
          </Button>
        </div>
        </div>
      </div>
    );
  }

  // ── Paywall (free or denied with pricing) ──
  if (access.canSeePricing || access.status === "denied") {
    const isExamRoute = pathname.includes('/real-exam') || pathname.includes('/exam-simulation');
    if (isExamRoute) {
      captureSentryEvent({
        level: "error",
        message: "paywall_during_exam",
        extra: {
          userId: access.input.user?.id,
          route: pathname,
          accessResult: { role: access.role, reason: access.reason },
          timestamp: new Date().toISOString(),
        },
        tags: { issue_type: "paywall_during_exam" },
      });
    }
    logEffectiveAccess("SubscriptionRoute", access);
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <PaywallNavHeader />
        <div className="flex items-center justify-center p-6">
        <Card className="max-w-2xl w-full p-8">
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center">
                <Lock className="w-10 h-10 text-destructive" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-[clamp(1.5rem,6vw,1.875rem)] font-bold text-foreground break-words">
                {t("subscription.requiredTitle")}
              </h1>
              <p className="text-lg text-muted-foreground">
                {t("subscription.requiredDesc")}
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-6 space-y-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-primary mt-0.5" />
                <div className="text-left">
                  <h3 className="font-semibold mb-2">
                    {t("subscription.benefitsTitle")}
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      {t("subscription.benefit1")}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      {t("subscription.benefit2")}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      {t("subscription.benefit3")}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      {t("subscription.benefit4")}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      {t("subscription.benefit5")}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      {t("subscription.benefit6")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {isNativeIOS ? (
              <NativeSubscriptionSurface language={language} />
            ) : (
              <>
                {isRealExamRoute && (
                  <div className="flex justify-center mt-2">
                    <Button type="button" onClick={() => void handleSyncAccess()} variant="secondary" size="lg" disabled={syncingAccess}>
                      {syncingAccess ? 'Sincronizando acceso…' : 'Sincronizar acceso'}
                    </Button>
                  </div>
                )}
                <PricingPlanSelector variant="full" />

                <div className="flex justify-center mt-2">
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location.href = "/dashboard";
                    }}
                    variant="outline"
                    size="lg"
                  >
                    {t("subscription.backToDashboard")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};